import { type ReactNode, useEffect, useRef } from 'react';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser';
import { msalConfig, tokenRequest, isAzureConfigured } from '@/config/msal';
import { getSupabaseClient } from '@/config/supabase';
import { useAuthStore } from '@/stores/useAuthStore';
import {
  fetchManagerBySupabaseUserId,
  exchangeAzureToken,
  finalizeProfile,
} from '@/services/auth';
import { LOG_PREFIX } from '@/config/constants';

// MSAL is always mounted so the Microsoft and email/password flows can coexist.
// With no Azure config (local dev) it constructs against a placeholder and the
// Microsoft button is simply hidden — see isAzureConfigured.
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * True when an error is a Supabase auth failure caused by a stale/invalid cached
 * session — a revoked or missing refresh token, an expired JWT, or a token minted
 * against a different project/user (e.g. the auth user was deleted, or the env was
 * switched). These shouldn't surface as an error screen; we clear the token and
 * fall back to a clean signed-out state instead.
 */
function isStaleSessionError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const { name, status, message } = err as {
    name?: unknown;
    status?: unknown;
    message?: unknown;
  };
  if (name === 'AuthApiError' || name === 'AuthSessionMissingError') return true;
  if (status === 400 || status === 401) return true;
  if (typeof message === 'string' && /refresh token|jwt|session/i.test(message)) return true;
  return false;
}

/**
 * Restores an existing session on mount and resolves the manager profile. Checks
 * the Supabase session first (covers email/password owners + super admins), then
 * falls back to an MSAL/Azure session. Login and signup actions set the profile
 * directly via useAuth, so this only handles initial restoration.
 */
function AuthInitializer({ children }: { children: ReactNode }) {
  const { instance, inProgress } = useMsal();
  const msalAuthenticated = useIsAuthenticated();
  const { setManager, setNoProfile, setLoading, setError } = useAuthStore();
  const ranRef = useRef(false);

  useEffect(() => {
    // Wait for any in-flight MSAL redirect to settle before deciding.
    if (isAzureConfigured && inProgress !== InteractionStatus.None) return;
    // Run exactly once. Every terminal action below writes to the Zustand store
    // (an external store), so there's no React state-after-unmount hazard and we
    // deliberately do NOT abort on unmount — under StrictMode the mount/cleanup/
    // remount cycle would otherwise cancel the only run and hang on the loader.
    if (ranRef.current) return;
    ranRef.current = true;

    // Clear any stale/invalid Supabase token and reset to a clean signed-out
    // state. AuthGuard then routes to /login. Also stops the bad token from
    // recurring on the next load (signOut removes it from storage).
    const recoverToSignedOut = async () => {
      const supabase = getSupabaseClient();
      await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      useAuthStore.getState().logout();
    };

    const resolve = async () => {
      try {
        const supabase = getSupabaseClient();
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        // A revoked/missing refresh token surfaces here (or throws below) — wipe it
        // and land signed-out rather than showing an auth error.
        if (sessionError && isStaleSessionError(sessionError)) {
          await recoverToSignedOut();
          return;
        }

        if (session?.user) {
          const profile = await fetchManagerBySupabaseUserId(session.user.id);
          if (!profile) {
            setNoProfile('supabase');
            return;
          }
          setManager(await finalizeProfile(profile), 'supabase');
          return;
        }

        if (msalAuthenticated) {
          const accounts = instance.getAllAccounts();
          if (accounts.length === 0) {
            setLoading(false);
            return;
          }
          const response = await instance.acquireTokenSilent({
            ...tokenRequest,
            account: accounts[0],
          });
          const profile = await exchangeAzureToken(response.accessToken);
          setManager(await finalizeProfile(profile), 'azure');
          return;
        }

        setLoading(false);
      } catch (err) {
        // Stale cached session (bad refresh token, deleted user, switched project):
        // recover silently to a clean signed-out state instead of an error screen.
        if (isStaleSessionError(err)) {
          await recoverToSignedOut();
          return;
        }
        console.error(LOG_PREFIX, 'Auth resolution failed:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    };

    resolve();
  }, [inProgress, msalAuthenticated, instance, setManager, setNoProfile, setLoading, setError]);

  return <>{children}</>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthInitializer>{children}</AuthInitializer>
    </MsalProvider>
  );
}
